import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { createContext, useEffect, useState } from 'react';
import 'styles/globals.less';
import { subscribe, unsubscribe } from '../src/utils/events';
import { getLocaleInfo, LocaleInfoTypes } from 'subdomains';
import { usePathname } from 'next/navigation';
import { checkLocaleData, hardRedirectTo } from 'src/utils';
import { unlockBodyScroll } from 'src/utils/dom-utils/utils';

const defaultContext = {
  theme: 'light',
  switchThemeCallback: (theme: string) => {},
  accordionAnchor: '',
};

export const VAAContext = createContext(defaultContext);

function MyApp({ Component, pageProps }: AppProps) {
  const pathName = usePathname();
  const router = useRouter();
  const switchTheme = (theme: string) => {
    setAppContext({ ...appContext, theme });
  };

  const [appContext, setAppContext] = useState({
    theme: 'light',
    switchThemeCallback: switchTheme,
    accordionAnchor: '',
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && router.asPath.includes('#')) {
      const accordionAnchor = router.asPath.split('#')[1];
      if (accordionAnchor) {
        setAppContext({ ...appContext, accordionAnchor });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  useEffect(() => {
    // Subscribe to locale change events
    subscribe('VAA_LOCALE_CHANGE', (e: any) => {
      const selectedLocale = checkLocaleData(e.detail.data);

      if (selectedLocale) {
        const subdomain = getLocaleInfo(selectedLocale, LocaleInfoTypes.URL);
        if (subdomain) {
          hardRedirectTo(subdomain);
        } else {
          if (
            selectedLocale !== 'en-GB' &&
            (pathName.toLowerCase() === '/holidays' ||
              pathName.toLowerCase().startsWith('/holidays/'))
          ) {
            /// fix640343 - For `/holidays/*` redirect non en-gb locales to their respective homepages
            hardRedirectTo(`/${selectedLocale}`);
          } else {
            hardRedirectTo(`/${selectedLocale}${pathName}`);
          }
        }
      }
    });

    // Subscribe to search panel close events
    subscribe('CLOSE_FLYOUT', () => {
      unlockBodyScroll();
    });

    return () => {
      unsubscribe('VAA_LOCALE_CHANGE', () => {});
      unsubscribe('CLOSE_FLYOUT', () => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSessionCookie = ({
    name,
    value,
    path = '/',
    sameSite = 'Lax',
    secure,
  }: {
    name: string;
    value: string;
    path?: string;
    sameSite?: 'Strict' | 'Lax' | 'None';
    secure?: boolean;
  }) => {
    if (typeof document === 'undefined') {
      return;
    }

    const cookieParts = [
      `${name}=${value}`,
      `path=${path}`,
      `SameSite=${sameSite}`,
    ];

    if (secure ?? window.location.protocol === 'https:') {
      cookieParts.push('Secure');
    }

    document.cookie = cookieParts.join('; ');
  };

  const getCookie = (name: string): string | null => {
    if (typeof document === 'undefined') {
      return null;
    }

    const prefix = `${name}=`;
    const match = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(prefix));

    return match ? match.substring(prefix.length) : null;
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const localeSlugMatch = window.location.pathname.match(
      /^\/([a-zA-Z]{2}-[a-zA-Z]{2})(?=\/|$)/
    );
    const localeFromPath = localeSlugMatch?.[1];
    const hasLocaleInPath = Boolean(localeFromPath);
    const defaultLocale = checkLocaleData('en-GB') ?? 'en-GB';
    const normalisedLocale = localeFromPath
      ? checkLocaleData(localeFromPath)
      : defaultLocale;

    if (!normalisedLocale) {
      return;
    }

    const cookieLocaleValue = getCookie('NEXT_LOCALE');
    const normalisedCookieLocale = cookieLocaleValue
      ? checkLocaleData(cookieLocaleValue)
      : null;

    const shouldSetLocaleCookie =
      !normalisedCookieLocale ||
      (hasLocaleInPath && normalisedCookieLocale !== normalisedLocale);

    if (!shouldSetLocaleCookie) {
      return;
    }

    setSessionCookie({
      name: 'NEXT_LOCALE',
      value: normalisedLocale,
      secure: window.location.protocol === 'https:',
    });
  }, [router.asPath]);

  return (
    <VAAContext.Provider value={appContext}>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, viewport-fit=cover"
        />
      </Head>
      <Component {...pageProps} />
    </VAAContext.Provider>
  );
}

export default MyApp;
